import React from 'react';
import { useTranslation } from 'react-i18next';
import { AreaInterest } from './areaInterestApi';
import { OffMarketWillingnessEvent as OffMarketWillingnessEventType } from '../../../utils/types';
import { formatPriceRange } from '../../../ListingCard/cardUtils';
import { buyerRoomsFloor } from '../../../ListingCard/buyerRoomsFloor';
import { formatStreetLine } from '../../../utils/addressFormatting';
import {
  AMENITY_OPTIONS,
  FLOOR_OPTIONS,
  RENOVATION_OPTIONS,
  criteriaOptionLabel,
} from './steps/criteriaOptions';
import './MyAreasView.scss';

/** Summarises the notify flags of an area interest as a short localised string. */
export const useNotifyLabel = (interest: AreaInterest): string => {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (interest.notify_email) parts.push(t('newLandingPage.couldBuy.myAreas.notifyEmail'));
  if (interest.notify_sms) parts.push(t('newLandingPage.couldBuy.myAreas.notifySms'));
  return parts.length > 0 ? parts.join(' + ') : t('newLandingPage.couldBuy.myAreas.notifyNone');
};

// Build a short human-readable summary of one area interest's criteria.

/**
 * The interest's price range as one label ("260 000–790 000 €"), or '' when no
 * price criteria are set. Split out so the small card can put it in its
 * bottom-left price slot, like the seller card's asking price, and reused for
 * the demand buyer card's price slot so the two read identically.
 */
export const criteriaPriceLabel = (interest: AreaInterest, short = false): string => {
  const { price_min, price_max } = interest.criteria;
  return formatPriceRange(price_min, price_max, short);
};

/**
 * The interest's size range as one label ("55–80 m²"), or '' when unset. Its own
 * export because the demand buyer card leads with size alone, while the small
 * card pairs it with rooms via criteriaStatsLabel.
 */
export const criteriaSizeLabel = (interest: AreaInterest): string => {
  const { size_min, size_max } = interest.criteria;
  if (size_min && size_max) return `${size_min}–${size_max} m²`;
  if (size_min) return `≥ ${size_min} m²`;
  if (size_max) return `≤ ${size_max} m²`;
  return '';
};

/**
 * The interest's desired features (floor, renovation, amenities) in the
 * could-buy vocabulary — not the seller listing one, so the labels stay correct.
 * Array form feeds the demand card's chip row; criteriaLabel joins it into the
 * small card's one-line summary.
 */
export const criteriaChips = (
  interest: AreaInterest,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string[] => {
  const { floor, renovation_status, amenities } = interest.criteria;
  const chips: string[] = [];
  const floorLabel = criteriaOptionLabel(FLOOR_OPTIONS, floor, t);
  if (floorLabel) chips.push(floorLabel);
  const renovationLabel = criteriaOptionLabel(RENOVATION_OPTIONS, renovation_status, t);
  if (renovationLabel) chips.push(renovationLabel);
  (amenities ?? []).forEach((value) => {
    const label = criteriaOptionLabel(AMENITY_OPTIONS, value, t);
    if (label) chips.push(label);
  });
  return chips;
};

/**
 * Size + rooms as one label ("55–80 m² · 3+ rooms"), or '' when neither is set.
 * Split out so the small card can use it as the headline, mirroring how a
 * building-level buy card leads with its stats.
 */
export const criteriaStatsLabel = (
  interest: AreaInterest,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const { rooms_min, rooms_flexible } = interest.criteria;
  const parts: string[] = [];
  const sizeLabel = criteriaSizeLabel(interest);
  if (sizeLabel) parts.push(sizeLabel);
  const roomFloor = buyerRoomsFloor(rooms_min ?? NaN, rooms_flexible);
  if (Number.isFinite(roomFloor) && roomFloor >= 1)
    parts.push(
      t('newLandingPage.couldBuy.myAreas.roomsValue', {
        count: roomFloor,
      }),
    );
  return parts.join(' · ');
};

export const criteriaLabel = (
  interest: AreaInterest,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const { property_description } = interest.criteria;
  const parts: string[] = [];
  const statsLabel = criteriaStatsLabel(interest, t);
  if (statsLabel) parts.push(statsLabel);
  const priceLabel = criteriaPriceLabel(interest);
  if (priceLabel) parts.push(priceLabel);
  parts.push(...criteriaChips(interest, t));
  if (property_description && parts.length === 0) return property_description.slice(0, 60);
  return parts.length > 0 ? parts.join(' · ') : t('newLandingPage.couldBuy.myAreas.anyCriteria');
};

// Build a short display label for the locations inside one area interest:
// "500 m radius, Kallio" or "Kamppi, Töölö" etc. When `anchorAddress` is given
// (the demand cards have it), the circle reads "<street> ± 500 m" so the radius
// is anchored to a real place instead of a bare "500 m radius".
export const areasLabel = (
  interest: Pick<AreaInterest, 'display_areas'>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  anchorAddress?: string,
): string => {
  const parts: string[] = [];
  // The anchor building (building-only interest) leads the line when present.
  interest.display_areas.forEach((a) => {
    if (a.kind === 'building') parts.push(a.name);
  });
  const hasCircle = interest.display_areas.some((a) => a.kind === 'circle');
  const districtNames = interest.display_areas
    .filter((a) => a.kind === 'area')
    .map((a) => (a as { name: string }).name);
  // A circle whose anchor building was since removed has no centre to name.
  // Say "500 m radius" only when it is the whole location — with districts
  // present they already answer "where", and the bare radius answers nothing.
  if (hasCircle && (anchorAddress || districtNames.length === 0)) {
    parts.push(
      anchorAddress
        ? t('newLandingPage.couldBuy.myAreas.circleAround', { address: anchorAddress })
        : t('newLandingPage.couldBuy.myAreas.circleLabel'),
    );
  }
  parts.push(...districtNames);
  return parts.join(', ') || t('newLandingPage.couldBuy.myAreas.noAreas');
};

interface AreaInterestCardProps {
  interest: AreaInterest;
  onOpen?: () => void;
  // Optional action controls (edit/delete) rendered in the card's right column.
  // Omitted on read-only surfaces like the could-buy success screen.
  actions?: React.ReactNode;
}

/**
 * Presentational listing-style card for one area interest: location line, criteria
 * summary, and notify row. Shared by My Listings (with edit/delete `actions`) and
 * the could-buy success screen (display-only), so both render identically.
 */
export const AreaInterestCard: React.FC<AreaInterestCardProps> = ({
  interest,
  onOpen,
  actions,
}) => {
  const { t } = useTranslation();
  const notifyLabel = useNotifyLabel(interest);
  const body = (
    <>
      <span className="my-areas__card-areas">
        {areasLabel(
          interest,
          t,
          interest.anchor_address?.street
            ? formatStreetLine(interest.anchor_address)
            : undefined,
        )}
      </span>
      <span className="my-areas__card-criteria">
        {criteriaLabel(interest, t)}
      </span>
      <span className="my-areas__card-notify">{notifyLabel}</span>
    </>
  );

  return (
    <div className="my-areas__card">
      {onOpen ? (
        <button
          type="button"
          className="my-areas__card-body my-areas__card-open"
          onClick={onOpen}
        >
          {body}
        </button>
      ) : (
        <div className="my-areas__card-body">{body}</div>
      )}
      {actions && <div className="my-areas__card-actions">{actions}</div>}
    </div>
  );
};

/**
 * Dress an area interest as a listing so it renders through the same small
 * buyer ListingCard as building-level could-buys — one card style, zero new
 * CSS. Mirrors the building buy card's slot order: size + rooms lead as the
 * headline, the areas drop to the description line, and the price range goes
 * to the card's price slot (passed separately as priceText). A stats-less
 * interest leads with its areas instead.
 *
 * Lives here rather than in MyListings because the could-buy success step
 * renders the same preview card from unsaved form state.
 *
 * `own` defaults to true (owner surfaces: My Listings, the success preview).
 * Pass false for anonymized demand rows shown to other users (the building
 * preview card), where marking the card as the viewer's own would wrongly
 * surface manage affordances.
 */
export const areaInterestToCardListing = (
  interest: AreaInterest,
  t: (key: string, opts?: Record<string, unknown>) => string,
  own = true,
): OffMarketWillingnessEventType => {
  const statsLabel = criteriaStatsLabel(interest, t);
  // Anchor street for the circle label: "Lönnrotinkatu 19 ± 500 m" instead of
  // a bare "500 m radius", which identifies nothing.
  const anchorStreet = interest.anchor_address
    ? [interest.anchor_address.street, interest.anchor_address.houseNumber]
        .filter(Boolean)
        .join(' ')
    : '';
  const placesLabel = areasLabel(interest, t, anchorStreet || undefined);
  return {
    _id: interest.area_interest_id,
    // Marks the card as an area interest rather than a listing, which is what
    // useOpenListing tests to keep the translated criteria label out of the URL.
    area_interest_id: interest.area_interest_id,
    buildingId: interest.anchor_building_id || '',
    isUserOwnEvent: own,
    could_sell: false,
    could_buy: true,
    last_modified_value: 'COULD_BUY',
    // The line below the headline is the buyer's own text only. Places never
    // land there: they lead the headline whenever there are no stats to take it,
    // and repeating them underneath tells the reader nothing new.
    property_description: interest.criteria.property_description ?? '',
    address: {
      street: statsLabel || placesLabel,
      houseNumber: '',
      city: '',
      country: '',
      region: '',
      postcode: '',
      coordinates: { lat: 0, lng: 0 },
    },
    updated_at: interest.created_at,
    verified: interest.verified,
  } as OffMarketWillingnessEventType;
};
